import { useTranslation } from 'react-i18next';
import { DatePicker } from 'antd';
import * as yup from 'yup';
import withModal from '../../../core/components/withModals';
import FormModalStyled, { Container } from '../../../core/components/modals/FormModalStyled';
import ATextInput from '../../../core/components/inputs/textInput/ATextInput';
import ASelect from '../../../core/components/select/ASelect';
import AForm from '../../../core/components/form/AForm';
import { useAuthStateValue } from '../../../context/AuthContext';
import { UserSpecialRoleEnum } from '../enums/userSpecialRoleEnum';
import ASwitch from '../../../core/components/inputs/switch/ASwitch';

const AddUserModal = (props) => {
  const { t } = useTranslation();
  const { loggedUser } = useAuthStateValue();
  const {
    modalData,
    modalProps,
    onSave,
    onEdit
  } = props;

  const mapLocationSelectOptions = () => {
    const options = [];
    loggedUser?.locationsData?.forEach((el) => {
      options.push({
        key: el?.key,
        title: el?.name,
        value: el?.key
      });
    });

    return options;
  }

  const mapRoleSelectOptions = () => {
    const options = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const [key] of Object.entries(UserSpecialRoleEnum)) {
      options.push({
        key,
        title: t('users.' + key),
        value: key
      });
    }

    return options;
  };

  const fields = [
    {
      id: 'firstName',
      name: t('users.firstName'),
      render: <ATextInput />
    },
    {
      id: 'lastName',
      name: t('users.lastName'),
      render: <ATextInput />
    },
    {
      id: 'email',
      name: t('users.email'),
      render: <ATextInput />
    },
    {
      id: 'ojti',
      name: t('users.ojti'),
      fieldType: 'switch',
      render: <ASwitch />
    }
  ];

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);

  const editValidationSchema = yup.object().shape({
    firstName: yup
        .string()
        .required(t('errors.required'))
        .min(3, `${t('errors.minLength')} ${3}.`)
        .max(24, `${t('errors.maxLength')} ${24}.`),
    lastName: yup
        .string()
        .required(t('errors.required'))
        .min(3, `${t('errors.minLength')} ${3}.`)
        .max(24, `${t('errors.maxLength')} ${24}.`),
    email: yup
        .string()
        .email(t('errors.invalidEmail'))
        .required(t('errors.required')),
    ojti: yup
        .boolean()
        .transform((value, originalValue) => {
          return originalValue === '' ? false : value;
        })
        .test({
          name: 'ojtiCheck',
          exclusive: true,
          params: { modalData },
          message: t('users.ojtiNotCheckedWhenStudent'),
          test: (value) => (value === false && modalData?.specialRole === 'STUDENT') || modalData?.specialRole !== 'STUDENT'
        }),
  });

  const validationSchema = yup.object().shape({
    firstName: yup
        .string()
        .required(t('errors.required'))
        .min(3, `${t('errors.minLength')} ${3}.`)
        .max(24, `${t('errors.maxLength')} ${24}.`),
    lastName: yup
        .string()
        .required(t('errors.required'))
        .min(3, `${t('errors.minLength')} ${3}.`)
        .max(24, `${t('errors.maxLength')} ${24}.`),
    email: yup
        .string()
        .email(t('errors.invalidEmail'))
        .required(t('errors.required')),
    locationId: yup
        .string()
        .required(t('errors.required')),
    specialRole: yup
        .string()
        .required(t('errors.required')),
    dateFrom: yup
        .date()
        .typeError(t('errors.invalidDate'))
        .required(t('errors.required'))
        .min(minDate, t('errors.dateInPast')),
    ojti: yup
        .boolean()
        .transform((value, originalValue) => {
          return originalValue === '' ? false : value;
        })
        .when('specialRole', {
          is: 'STUDENT',
          then: (schema) => schema.equals([false], t('users.ojtiNotCheckedWhenStudent')),
          otherwise: (schema) => schema,
        }),
  });

  if (!modalData) {
    fields.splice(1, 0, {
      id: 'fatherName',
      name: t('users.fatherName'),
      render: <ATextInput />
    });
    fields.splice(4, 0, {
      id: 'locationId',
      name: t('users.location'),
      render: <ASelect options={mapLocationSelectOptions()} />,
    });
    fields.splice(5, 0, {
      id: 'specialRole',
      name: t('users.specialRole'),
      render: <ASelect options={mapRoleSelectOptions()} />,
    });
    fields.splice(7, 0, {
      id: 'dateFrom',
      name: t('users.dateFrom'),
      render: <DatePicker />
    })
  }

  return (
    <FormModalStyled
      centered
      maskClosable={false}
      width={550}
      footer={[]}
      {...modalProps}
      title={modalData ? t('users.editUserTitle') : t('users.addUser')}
    >
      <Container style={{ paddingRight: '20px' }}>
        <AForm
          fields={fields}
          modalData={modalData}
          okButtonText={t('buttons.confirm')}
          validationSchema={modalData ? editValidationSchema : validationSchema}
          modalAddHandler={onSave}
          modalEditHandler={onEdit}
          name="add-user-modal-form"
        />
      </Container>
    </FormModalStyled>
  );
};

export default withModal()(AddUserModal);
